import { useGeneralStore } from '@/stores/general';
import type { CartItem, EventDetail, Order, Ticket } from '@/types';

import { getConsent } from './consent';
import { pushToDataLayer } from './dataLayer';
import { trackFBEvent } from './fbPixel';

// ── Helpers ──

/** Prices from the backend are in cents (øre). Analytics expects main currency units. */
function centsToUnits(cents: number): number {
  return cents / 100;
}

function formatPrice(cents: number): string {
  return centsToUnits(cents).toFixed(2);
}

function getBrand(): string {
  try {
    return useGeneralStore().customer().name ?? '';
  } catch {
    return '';
  }
}

// ── GA4 item format ──

interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  price: number;
  quantity: number;
  currency?: string;
}

function ticketsToGA4Items(cartItem: CartItem): GA4Item[] {
  return cartItem.tickets
    .filter((t: Ticket) => t.count > 0)
    .map((t: Ticket) => ({
      item_id: String(t.id),
      item_name: t.name1,
      item_category: cartItem.name,
      price: centsToUnits(t.price),
      quantity: t.count,
      currency: cartItem.currency,
    }));
}

function allItemsFromBasket(basket: CartItem[]): GA4Item[] {
  return basket.flatMap(ticketsToGA4Items);
}

function calculateBasketTotal(basket: CartItem[]): number {
  return basket.reduce((sum, item) => {
    return (
      sum +
      item.tickets.reduce((ticketSum, t) => ticketSum + centsToUnits(t.price) * t.count, 0)
    );
  }, 0);
}

// ── GTM Enhanced Ecommerce product format (backward compatible) ──

interface EEProduct {
  id: number | string;
  name: string;
  price: string;
  brand: string;
  quantity?: number;
  category?: string;
}

function ticketToEEProduct(cartItem: CartItem, ticket: Ticket, quantity?: number): EEProduct {
  return {
    id: ticket.id,
    name: `${cartItem.name} - ${ticket.name1}`,
    price: formatPrice(ticket.price),
    brand: getBrand(),
    quantity: quantity ?? ticket.count,
  };
}

function basketToEEProducts(basket: CartItem[]): EEProduct[] {
  return basket.flatMap((item) =>
    item.tickets
      .filter((t) => t.count > 0)
      .map((t) => ticketToEEProduct(item, t)),
  );
}

// ── Public tracking functions ──

/**
 * Track `view_item` (GA4) + `EEproductView` (GTM dataLayer).
 */
export function trackViewItem(event: EventDetail): void {
  const currency = event.currency ?? 'DKK';
  const priceUnits = centsToUnits(event.price);

  // GA4
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency,
      value: priceUnits,
      items: [
        {
          item_id: String(event.id),
          item_name: event.info1,
          price: priceUnits,
          quantity: 1,
          currency,
        },
      ],
    });
  }

  // GTM dataLayer: EEproductView
  const brand = getBrand();
  const products: EEProduct[] = [
    {
      id: event.id,
      name: event.info1,
      price: formatPrice(event.price),
      brand,
    },
  ];
  pushToDataLayer({
    event: 'EEproductView',
    ecommerce: {
      currencyCode: currency,
      detail: {
        actionField: { list: '' },
        products,
      },
    },
  });

  // FB Pixel: ViewContent
  if (getConsent()?.marketing) {
    trackFBEvent('ViewContent', {
      content_ids: [String(event.id)],
      content_name: event.info1,
      content_type: 'product',
      value: priceUnits,
      currency,
    });
  }
}

/**
 * Track `EEproductClick` (GTM dataLayer) when user clicks an event card.
 */
export function trackProductClick(event: { id: number | string; info1: string; price: number; currency?: string }): void {
  const currency = event.currency ?? 'DKK';
  const brand = getBrand();

  pushToDataLayer({
    event: 'EEproductClick',
    ecommerce: {
      currencyCode: currency,
      click: {
        actionField: { list: '' },
        products: [
          {
            id: event.id,
            name: event.info1,
            price: formatPrice(event.price),
            brand,
          },
        ],
      },
    },
  });
}

/**
 * Track `add_to_cart` (GA4) + `EEaddToCart` (GTM dataLayer).
 * Called with per-ticket granularity: only the tickets that changed.
 */
export function trackAddToCart(cartItem: CartItem, ticket: Ticket, quantityAdded: number): void {
  const currency = cartItem.currency;
  const priceUnits = centsToUnits(ticket.price);
  const value = priceUnits * quantityAdded;

  // GA4
  if (window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency,
      value,
      items: [
        {
          item_id: String(ticket.id),
          item_name: ticket.name1,
          item_category: cartItem.name,
          price: priceUnits,
          quantity: quantityAdded,
          currency,
        },
      ],
    });
  }

  // GTM dataLayer: EEaddToCart
  pushToDataLayer({
    event: 'EEaddToCart',
    ecommerce: {
      currencyCode: currency,
      add: {
        products: [ticketToEEProduct(cartItem, ticket, quantityAdded)],
      },
    },
  });

  // FB Pixel: AddToCart
  if (getConsent()?.marketing) {
    trackFBEvent('AddToCart', {
      content_ids: [String(ticket.id)],
      content_name: cartItem.name,
      content_type: 'product',
      value,
      currency,
    });
  }
}

/**
 * Track `remove_from_cart` (GA4) + `EEremoveFromCart` (GTM dataLayer).
 * Called with per-ticket granularity.
 */
export function trackRemoveFromCart(cartItem: CartItem, ticket: Ticket, quantityRemoved: number): void {
  const currency = cartItem.currency;
  const priceUnits = centsToUnits(ticket.price);
  const value = priceUnits * quantityRemoved;

  // GA4
  if (window.gtag) {
    window.gtag('event', 'remove_from_cart', {
      currency,
      value,
      items: [
        {
          item_id: String(ticket.id),
          item_name: ticket.name1,
          item_category: cartItem.name,
          price: priceUnits,
          quantity: quantityRemoved,
          currency,
        },
      ],
    });
  }

  // GTM dataLayer: EEremoveFromCart
  pushToDataLayer({
    event: 'EEremoveFromCart',
    ecommerce: {
      currencyCode: currency,
      remove: {
        products: [ticketToEEProduct(cartItem, ticket, quantityRemoved)],
      },
    },
  });
}

/**
 * Track `begin_checkout` (GA4) + `EEcheckout` step 1 (GTM dataLayer).
 */
export function trackBeginCheckout(basket: CartItem[]): void {
  const items = allItemsFromBasket(basket);
  const value = calculateBasketTotal(basket);
  const currency = basket[0]?.currency ?? 'DKK';

  // GA4
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency,
      value,
      items,
    });
  }

  // GTM dataLayer: EEcheckout step 1
  pushToDataLayer({
    event: 'EEcheckout',
    ecommerce: {
      checkout: {
        actionField: { step: 1, option: 'Customer informations' },
        products: basketToEEProducts(basket),
      },
    },
  });

  // FB Pixel: InitiateCheckout
  if (getConsent()?.marketing) {
    trackFBEvent('InitiateCheckout', {
      content_ids: items.map((i) => i.item_id),
      num_items: items.reduce((sum, i) => sum + i.quantity, 0),
      value,
      currency,
    });
  }
}

/**
 * Track `EEcheckout` step 2 (GTM dataLayer) when user enters information page.
 */
export function trackCheckoutStep2(): void {
  pushToDataLayer({
    event: 'EEcheckout',
    ecommerce: {
      checkout_option: {
        actionField: { step: 2, option: 'Customer information' },
      },
      checkout: {
        actionField: { step: 2 },
      },
    },
  });
}

/**
 * Track `purchase` (GA4) + `EEtransaction` (GTM dataLayer).
 */
export function trackPurchase(order: Order): void {
  const items = allItemsFromBasket(order.basket);
  const value = calculateBasketTotal(order.basket);
  const currency = order.basket[0]?.currency ?? 'DKK';

  // GA4
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: String(order.orderid),
      value,
      currency,
      items,
    });
  }

  // GTM dataLayer: EEtransaction
  const eeProducts = basketToEEProducts(order.basket);
  const shippingFee = order.basket.reduce((fee, item) => {
    const ticketWithShipping = item.tickets.find((t) => t.shippingfee > 0);
    return ticketWithShipping ? centsToUnits(ticketWithShipping.shippingfee) : fee;
  }, 0);

  pushToDataLayer({
    event: 'EEtransaction',
    ecommerce: {
      purchase: {
        actionField: {
          id: order.orderid,
          affiliation: getBrand(),
          revenue: value.toFixed(2),
          shipping: shippingFee.toFixed(2),
        },
        products: eeProducts,
      },
    },
  });

  // FB Pixel: Purchase
  if (getConsent()?.marketing) {
    trackFBEvent('Purchase', {
      content_ids: items.map((i) => i.item_id),
      content_type: 'product',
      num_items: items.reduce((sum, i) => sum + i.quantity, 0),
      value,
      currency,
    });
  }
}
